/**
 * Логика FILEATTACHMENTS для ElementRenderer: upload, RPC getData/getFullData, download, appendBytes.
 *
 * WUI-316: `uploadPendingAttachmentsForGetFullData` (блок по фазам R7, promote по снимку).
 * `handleOfficeCallbackStatus` (WS → session + persist `storage_uri`),
 * `handleAwaitingWsTimeout` (sync-state / discard), `fileAttachmentHooks.onOfficeCallbackStatus`.
 *
 * @see dev/WUI-293_todo.md — §3.2 декомпозиция ElementRenderer
 * @see dev/WUI-316.md §4.5
 */
import type { Ref, ShallowRef } from 'vue'
import type { FileItem } from '@/common/types/fileItem'
import {
  attachmentUploadDebugWarn,
  explainAttachmentR7PolicyForTape,
  isAttachmentUploadDebugEnabled,
  logAttachmentStoreDebug,
  logAttachmentUploadPolicyForGetFullData,
  logR7AttachmentPrepareDebug,
  summarizeAttachmentFileItems,
} from '@/common/utils/attachmentDebugLog'
import {
  markAttachmentStoreMutationCause,
  type AttachmentStoreMutationCause,
} from '@/common/utils/attachmentStoreMutationCause'
import {
  AttachmentUploadError,
  formatAttachmentUploadError,
  uploadAttachmentItemSync,
} from '@/common/utils/attachmentUpload'
import { tGlobal } from '@/common/composables/useI18n'
import {
  canOpenOrViewAttachmentInR7Editor,
  canPreviewAttachmentInR7Inline,
  getAttachmentR7EditBlockReason,
  isAttachmentR7PreviewAwaitingFinalSave,
  isAttachmentR7EditBlocked,
  isAttachmentR7GetFullDataHardBlocked,
  parseFileAttachmentsElementMeta,
  resolveAttachmentR7EditorMode,
  isAttachmentPendingUploadMissingLocalFile,
  shouldUploadAttachmentOnGetFullData,
  shouldUploadDsStagedAttachmentOnGetFullData,
  shouldUseInlineAttachmentGetData,
  resolveAttachmentDisplayFileName,
} from '@/common/utils/attachmentRpcPolicy'
import { resolveAttachmentUploadSessionId } from '@/common/utils/attachmentUploadSession'
import {
  classifyFormSaveDocumentSyncState,
  r7FormSaveWaitBlocksGetFullData,
  type R7FormSaveWaitResult,
  waitUntilAttachmentsR7NotBlockingGetFullData,
} from '@/common/utils/r7AttachmentFormSaveWait'
import {
  isOfficeCallbackNoChanges,
  isOfficeCallbackSaveReady,
  isOfficeCallbackTerminalError,
  type OfficeCallbackStatusEvent,
} from '@/services/r7AttachmentEditor.service'
import {
  discardOfficeAttachmentDocument,
  fetchOfficeAttachmentDocumentSyncState,
  fetchOfficeAttachmentForceSave,
  formatOfficeEditorForceSaveError,
} from '@/services/r7AttachmentEditor.service'
import {
  clearSessionForAttachmentRow,
  findSessionForAttachmentItem,
  markSessionErrorAbandoned,
  onWsCallback,
  reconcileSessionStaged,
  registerR7AwaitingWsTimeoutHandler,
  resolveAttachmentDocumentSessionId,
  unregisterR7AwaitingWsTimeoutHandler,
  resetAwaitingWsTimer,
  type R7AttachmentEditSession,
} from '@/common/utils/r7AttachmentEditSession'
import { showR7SaveInProgressDialog } from '@/common/utils/r7SaveInProgressDialog'
import { dispatchR7EditorOfficeCallback } from '@/common/utils/r7EditorOfficeCallbackSession'
import {
  getDsAttachmentStore,
  resolveAttachmentRowForOfficeWsEvent,
  type DsAttachmentPhase,
} from '@/common/composables/useDsAttachmentState'
import {
  getAttachmentNativeFileStore,
  hasNativeFileForAttachmentRow,
  hydrateAttachmentFileItemsFromStore,
  resolveAttachmentItemsForUpload,
  resolvePositiveElementId,
} from '@/common/utils/attachmentNativeFileRegistry'
import {
  buildInlineAttachmentElementUniData,
  fileItemsToUniDataTable,
  getScalarFromFields,
  alignAttachmentClientRowKey,
  attachmentRowDownloadMetaEqual,
  findMatchingAttachmentItem,
  isNewLocalAttachmentItem,
  mergeUploadedRowResponse,
  type PlatformDownloadMeta,
  resolveAttachmentRowCodeVersion,
  rowAttachmentKey,
  snapshotAttachmentRowFields,
  uniDataTableToFileItems,
  withAttachmentItemChanged,
  overlayAttachmentItemsTempFromSources,
} from '@/common/utils/fileAttachmentsUniData'
import { mergePageDataFieldSnapshot } from '@/common/utils/resolveClientDataActualFields'
import { collectAttachmentPutFullDataWireRows } from '@/common/utils/attachmentPutFullDataWireBuffer'
import { applyPlatformDownloadMeta, fetchPlatformAttachmentDownload } from '@/common/utils/platformDownload'
import { useNotification } from '@/common/composables/useNotification'
import { getPlatformFileSessionContext } from '@/common/utils/platformFileSession'
import { resolveAttachmentTapeRowBadge } from '@/common/utils/attachmentTapeRowStatus'
import type { AttachmentTapeBadgeUi } from '@/common/utils/attachmentTapeRowStatus'
import type UniData from '@/common/classes/UniData.ts'

export type MFileInputExpose = {
  getAttachmentFileItems?: () => FileItem[]
  getFocusedAttachmentItem?: () => FileItem | null
  swingGetData?: (items?: FileItem[]) => UniData
  syncAttachmentFileItems?: (items: FileItem[]) => void
  appendFile?: (file: File) => void
  appendFileCopies?: (files: FileItem[]) => void
}

export type FileAttachmentsElementDeps = {
  elementType: Ref<string>
  fieldName: Ref<string>
  verificationError: Ref<string | null>
  elementConfig: Ref<unknown>
  formPath: Ref<{ formId?: unknown; elementId?: number | null } | undefined>
  elementId: Ref<number | null>
  componentRef: ShallowRef<MFileInputExpose | null>
  pageActions: Ref<readonly unknown[]>
  pageData: { value: unknown }
  pageRegistryFormId: Ref<string>
  pageDataSlot: Ref<string>
  currentPlatform: { value: string | null | undefined }
  appStorePagesData: Record<string, unknown>
  formProvider?: { getElements?: () => Record<string, unknown> }
  getSessionId: () => string | null
  getModelValue: () => unknown
  writeToElement: (table: ReturnType<typeof fileItemsToUniDataTable>) => void
  emitInput: (items: FileItem[]) => void
  /** WS save (status 2/6) или sync-state ready — обновить inline preview (WUI-317). */
  onR7AttachmentStagingUpdated?: (payload: {
    code: number
    version: number
    storageUri: string
    documentKey?: string
  }) => void
  /** Перед upload на getFullData: закрыть inline preview / dialog (избежать MW 4013). */
  onBeforeGetFullDataUpload?: () => void
  /** После getFullData upload (успех или abort) — снять hold preview на form-save. */
  onAfterGetFullDataUpload?: () => void
}

/** Стабильный ключ строки для сопоставления при upload / R7 staging. */
function pendingAttachmentUploadKey(item: FileItem): string {
  if (item.file instanceof File) {
    return `file:${item.file.name}:${item.file.size}:${item.file.lastModified}`
  }
  const fields = (item.fields ?? {}) as Record<string, unknown>
  const key = rowAttachmentKey(fields)
  if (key) return key
  const f = item.fields as Record<string, { value?: unknown }> | undefined
  return `row:${String(f?.name?.value ?? f?.fileName?.value ?? '')}`
}

export function useFileAttachmentsElement(deps: FileAttachmentsElementDeps) {
  const notification = useNotification()
  let registeredTimeoutElementId: number | null = null

  function resolveUploadElementId(): number | null {
    return resolvePositiveElementId(deps.formPath.value?.elementId, deps.elementId.value)
  }

  function resolveNativeFileRegistry() {
    const eid = resolveUploadElementId()
    if (eid == null) return null
    try {
      return getAttachmentNativeFileStore(eid)
    } catch {
      return null
    }
  }

  function collectWireAttachmentRows(): FileItem[] {
    const fieldName = String(deps.fieldName.value ?? '').trim()
    const rows: FileItem[] = [...uniDataTableToFileItems(deps.getModelValue())]
    const pages: unknown[] = [deps.pageData.value]
    const slot = String(deps.pageDataSlot.value ?? '').trim()
    if (slot) pages.push(deps.appStorePagesData[slot])
    if (!fieldName) return rows
    for (const page of pages) {
      if (!page || typeof page !== 'object') continue
      const snapshot = mergePageDataFieldSnapshot(page)
      rows.push(...uniDataTableToFileItems(snapshot[fieldName]))
    }
    rows.push(...collectAttachmentPutFullDataWireRows(fieldName))
    return rows
  }

  function overlayTapeTempFields(items: FileItem[]): FileItem[] {
    const uploadElementId = resolveUploadElementId()
    const overlaid = overlayAttachmentItemsTempFromSources(items, {
      wireRows: collectWireAttachmentRows(),
      resolveSnapshot: (item) => {
        if (uploadElementId == null) return null
        try {
          const snap = getDsAttachmentStore(uploadElementId).getSnapshot(item)
          if (!snap) return null
          return {
            storageUri: snap.storageUri,
            documentSessionId: snap.documentSessionId,
          }
        } catch {
          return null
        }
      },
    })
    return hydrateAttachmentFileItemsFromStore(uploadElementId, overlaid)
  }

  /** Текущие строки вложений для upload / getFullData (store + реестр `File`). */
  function getAttachmentItemsForUpload(): FileItem[] {
    const fromComponent = deps.componentRef.value?.getAttachmentFileItems?.()
    return resolveAttachmentItemsForUpload(
      resolveUploadElementId(),
      uniDataTableToFileItems(deps.getModelValue()),
      fromComponent,
    )
  }

  function hasUploadableNativeFileForFormSave(): boolean {
    const eid = resolveUploadElementId()
    const items = getAttachmentItemsForUpload()
    return items.some(
      (item) => item.file instanceof File || hasNativeFileForAttachmentRow(item, eid),
    )
  }

  /** UniData в store + реестр нативных файлов + `update:modelValue`. */
  function applyAttachmentItemsToStore(
    items: FileItem[],
    cause: AttachmentStoreMutationCause = 'user-input',
    causeDetail?: Record<string, unknown>,
  ) {
    const elementId = resolveUploadElementId()
    const fieldName = deps.fieldName.value
    markAttachmentStoreMutationCause({
      fieldName,
      elementId,
      cause,
      detail: causeDetail,
    })
    logAttachmentStoreDebug('apply-to-store', {
      fieldName,
      elementId,
      cause,
      ...(causeDetail ? { causeDetail } : {}),
      ...summarizeAttachmentFileItems(items),
    })
    resolveNativeFileRegistry()?.syncFromItems(items)
    deps.writeToElement(fileItemsToUniDataTable(items))
    deps.emitInput(items)
  }

  function notifyR7AttachmentStagingUpdated(
    code: number,
    version: number,
    storageUri: string,
    documentKey?: string,
  ): void {
    const uri = String(storageUri ?? '').trim()
    if (!uri) return
    deps.onR7AttachmentStagingUpdated?.({ code, version, storageUri: uri, documentKey })
  }

  function resolveDsStore(): ReturnType<typeof getDsAttachmentStore> | null {
    const uploadElementId = resolveUploadElementId()
    if (uploadElementId == null) return null
    return getDsAttachmentStore(uploadElementId)
  }

  /** Очистить DS-снимок после discard/error (R6) — re-open через prepare. */
  function recoverAttachmentAfterError(item: FileItem): void {
    const store = resolveDsStore()
    if (!store) return
    store.clearSnapshot(item)
  }

  function collectAttachmentSessionContext() {
    const registeredName = String(deps.fieldName.value ?? '').trim()
    const reg =
      registeredName && deps.formProvider?.getElements
        ? (deps.formProvider.getElements()?.[registeredName] as {
            formId?: unknown
            platformSessionId?: unknown
          })
        : undefined
    const elementIdForSession =
      deps.formPath.value?.elementId ?? deps.elementId.value ?? null
    const formIdRaw = deps.formPath.value?.formId
    const toFormId = (value: unknown): string | null => {
      const n = String(value ?? '').trim()
      return n || null
    }
    return {
      fieldName: registeredName || null,
      formId: toFormId(formIdRaw),
      pageRegistryFormId: deps.pageRegistryFormId.value || null,
      pageDataSlot: deps.pageDataSlot.value || null,
      elementFormId: toFormId(reg?.formId),
      platformSessionIdFromRegistry: reg?.platformSessionId,
      pageData: deps.pageData.value,
      pagesDataSnapshot: deps.appStorePagesData,
      elementId: elementIdForSession,
    }
  }

  /**
   * Перед getFullData: блок всех строк при `awaiting_ws`/`error`,
   * upload локальных и R7 promote. После успеха — `clearR7PromoteCandidateForCode`, `clearSession`.
   */
  function uploadPendingAttachmentsForGetFullData(): FileItem[] {
    if (deps.elementType.value !== 'FILEATTACHMENTS') {
      return getAttachmentItemsForUpload()
    }

    try {
      deps.onBeforeGetFullDataUpload?.()

      const elementMeta = parseFileAttachmentsElementMeta(deps.elementConfig.value)
      const sessionContext = collectAttachmentSessionContext()
      const resolved = resolveAttachmentUploadSessionId(sessionContext)
      const sessionId = resolved.sessionId ?? deps.getSessionId()
      const uploadElementId = resolveUploadElementId()
      const elementName = deps.fieldName.value

      let currentItems = overlayTapeTempFields([...getAttachmentItemsForUpload()])

    const blocked = currentItems.filter((item) =>
      isAttachmentR7GetFullDataHardBlocked(item, elementMeta, uploadElementId),
    )
    if (blocked.length > 0) {
      const reason =
        getAttachmentR7EditBlockReason(blocked[0]!, uploadElementId) ??
        tGlobal('attachments.r7WaitFormSave')
      deps.verificationError.value = reason
      notification.error({ summary: tGlobal('attachments.r7FormSaveSummary'), detail: reason })
      throw new AttachmentUploadError(reason)
    }

    const pending = currentItems.filter((item) =>
      shouldUploadAttachmentOnGetFullData(item, elementMeta, uploadElementId),
    )

    logAttachmentUploadPolicyForGetFullData({
      elementName,
      elementMeta,
      items: currentItems,
      pendingCount: pending.length,
      sessionId,
      uploadElementId,
    })

    if (!sessionId || uploadElementId == null || !elementName) {
      if (pending.length > 0) {
        const missing: string[] = []
        if (!sessionId) missing.push('sessionId')
        if (uploadElementId == null) missing.push('elementId')
        if (!elementName) missing.push('elementName')
        const f5Hint = !sessionId
          ? tGlobal('attachments.uploadMissingContextF5Hint')
          : ''
        throw new AttachmentUploadError(
          tGlobal('attachments.uploadMissingContext', { missing: missing.join(', ') }) + f5Hint,
        )
      }
      return currentItems
    }

    if (!pending.length) {
      const stuck = currentItems.filter((item) =>
        isAttachmentPendingUploadMissingLocalFile(item, elementMeta, uploadElementId),
      )
      if (stuck.length > 0) {
        const msg = tGlobal('attachments.uploadPendingMissingFile')
        deps.verificationError.value = msg
        notification.error({ summary: tGlobal('attachments.uploadError'), detail: msg })
        throw new AttachmentUploadError(msg)
      }
      return currentItems
    }

    attachmentUploadDebugWarn(`загрузка ${pending.length} файл(ов)…`, {
      elementName,
      uploadElementId,
    })

    const session = getPlatformFileSessionContext(
      deps.pageData.value as { isClone?: boolean; cloneOf?: string | null },
      sessionId,
      deps.currentPlatform.value,
    )

    for (const item of pending) {
      const key = pendingAttachmentUploadKey(item)
      const uploadItem =
        item.file instanceof File
          ? item
          : (hydrateAttachmentFileItemsFromStore(uploadElementId, [item])[0] ?? item)
      try {
        const serverRow = uploadAttachmentItemSync({
          item: uploadItem,
          elementId: uploadElementId,
          elementName,
          session,
        })
        if (serverRow == null) {
          if (shouldUploadDsStagedAttachmentOnGetFullData(item, elementMeta, uploadElementId)) {
            const msg = tGlobal('attachments.r7PromoteFailed')
            deps.verificationError.value = msg
            notification.error({ summary: tGlobal('attachments.r7PromoteSummary'), detail: msg })
            throw new AttachmentUploadError(msg)
          }
          attachmentUploadDebugWarn('R7 promote: staging ещё нет, строка без изменений', {
            elementName,
            uploadElementId,
          })
          continue
        }
        const merged = mergeUploadedRowResponse(serverRow, uploadItem)
        const itemFields = (item.fields ?? {}) as Record<string, unknown>
        if (shouldUploadDsStagedAttachmentOnGetFullData(item, elementMeta, uploadElementId)) {
          logR7AttachmentPrepareDebug('after-upload-merge', {
            elementId: uploadElementId,
            elementName,
            before: snapshotAttachmentRowFields((item.fields ?? {}) as Record<string, unknown>),
            after: snapshotAttachmentRowFields((merged.fields ?? {}) as Record<string, unknown>),
            serverRowKeys: Object.keys(
              serverRow.fields && typeof serverRow.fields === 'object'
                ? (serverRow.fields as Record<string, unknown>)
                : serverRow,
            ),
          })
        }
        const uploadEid = resolveUploadElementId()
        if (uploadEid != null) {
          const dsStore = getDsAttachmentStore(uploadEid)
          dsStore.resetAfterPromote(item)
          const mergedFields = (merged.fields ?? {}) as Record<string, unknown>
          const code = Number(
            (mergedFields.code as { value?: unknown })?.value ??
              itemFields.code ??
              mergedFields.code ??
              NaN,
          )
          if (Number.isFinite(code) && code > 0) {
            const version = Number(
              (mergedFields.version as { value?: unknown })?.value ??
                itemFields.version ??
                mergedFields.version ??
                0,
            )
            clearSessionForAttachmentRow(uploadEid, code, Number.isFinite(version) ? version : 0)
          }
        }
        currentItems = currentItems.map((row) => {
          if (pendingAttachmentUploadKey(row) === key) return merged
          const rowFields = (row.fields ?? {}) as Record<string, unknown>
          const itemCv = resolveAttachmentRowCodeVersion(itemFields)
          const rowCv = resolveAttachmentRowCodeVersion(rowFields)
          if (itemCv && rowCv && itemCv.code === rowCv.code) return merged
          return row
        })
      } catch (error) {
        const message = formatAttachmentUploadError(error)
        if (message) {
          deps.verificationError.value = message
        }
        throw error instanceof AttachmentUploadError
          ? error
          : new AttachmentUploadError(message || tGlobal('attachments.uploadError'))
      }
    }

    applyAttachmentItemsToStore(currentItems, 'upload-merge')
    deps.componentRef.value?.syncAttachmentFileItems?.(currentItems)
    deps.verificationError.value = null
    return currentItems
    } finally {
      deps.onAfterGetFullDataUpload?.()
    }
  }

  function abortIfR7FormSaveWaitNotReady(result: R7FormSaveWaitResult): void {
    if (!r7FormSaveWaitBlocksGetFullData(result)) return
    const reason =
      result === 'error'
        ? tGlobal('attachments.r7EditSaveFailed')
        : tGlobal('attachments.r7WaitFormSave')
    deps.verificationError.value = reason
    notification.error({ summary: tGlobal('attachments.r7FormSaveSummary'), detail: reason })
    throw new AttachmentUploadError(reason)
  }

  function resolvePlatformSessionForOfficeEditor(): ReturnType<
    typeof getPlatformFileSessionContext
  > | null {
    const sessionContext = collectAttachmentSessionContext()
    const resolved = resolveAttachmentUploadSessionId(sessionContext)
    const sessionId = resolved.sessionId ?? deps.getSessionId()
    if (!sessionId) return null
    return getPlatformFileSessionContext(
      deps.pageData.value as { isClone?: boolean; cloneOf?: string | null },
      sessionId,
      deps.currentPlatform.value,
    )
  }

  /** Form-save: middle Command Service. `true` only if HTTP succeeded. */
  async function requestOfficeEditorForceSave(documentSessionId: string | null | undefined): Promise<boolean> {
    const id = String(documentSessionId ?? '').trim()
    const uploadElementId = resolveUploadElementId()
    const platformSession = resolvePlatformSessionForOfficeEditor()
    if (!id || uploadElementId == null || !platformSession) {
      console.warn('[R7 Command Service] form-save skipped: no documentSessionId')
      return false
    }
    try {
      await fetchOfficeAttachmentForceSave({
        elementId: uploadElementId,
        documentSessionId: id,
        session: platformSession,
      })
      return true
    } catch (error) {
      const detail = formatOfficeEditorForceSaveError(error)
      console.warn('[R7 Command Service] form-save HTTP failed', detail)
      throw new AttachmentUploadError(detail)
    }
  }

  /** После close Р7: ждать staged/idle, не слать getFullData в awaiting_ws. */
  async function waitUntilR7NotBlockingGetFullData(options?: {
    onForceSaveRetry?: () => void
  }): Promise<R7FormSaveWaitResult> {
    if (deps.elementType.value !== 'FILEATTACHMENTS') return 'ready'
    const items = getAttachmentItemsForUpload()
    if (items.length === 0) return 'ready'
    return waitUntilAttachmentsR7NotBlockingGetFullData({
      items,
      meta: parseFileAttachmentsElementMeta(deps.elementConfig.value),
      elementId: resolveUploadElementId(),
      onReconcile: reconcileR7FormSaveBlockedRows,
      onForceSaveRetry: options?.onForceSaveRetry,
    })
  }

  /** Form-save wait: sync-state without «Подождать» dialog (keep the same action). */
  async function reconcileR7FormSaveBlockedRows(): Promise<void> {
    const uploadElementId = resolveUploadElementId()
    if (uploadElementId == null) return
    const sessionContext = collectAttachmentSessionContext()
    const resolved = resolveAttachmentUploadSessionId(sessionContext)
    const sessionId = resolved.sessionId ?? deps.getSessionId()
    if (!sessionId) return
    const platformSession = getPlatformFileSessionContext(
      deps.pageData.value as { isClone?: boolean; cloneOf?: string | null },
      sessionId,
      deps.currentPlatform.value,
    )
    const meta = parseFileAttachmentsElementMeta(deps.elementConfig.value)
    const items = getAttachmentItemsForUpload()
    const blocked = items.filter((item) => isAttachmentR7EditBlocked(item, meta, uploadElementId))
    const dsStore = getDsAttachmentStore(uploadElementId)

    for (const item of blocked) {
      const session = findSessionForAttachmentItem(item, uploadElementId)
      const documentSessionId = session?.documentSessionId?.trim()
      if (!session || !documentSessionId) continue
      try {
        const status = await fetchOfficeAttachmentDocumentSyncState({
          elementId: uploadElementId,
          documentSessionId,
          session: platformSession,
        })
        const classified = classifyFormSaveDocumentSyncState(status)
        if (classified === 'ready') {
          const resolvedUri = status.storageUri?.trim() || session.lastStorageUri?.trim() || ''
          reconcileSessionStaged(session.documentKey, resolvedUri, status.dsStatus)
          if (resolvedUri && status.documentKey) {
            dsStore.applyWsStaged(item, {
              documentKey: status.documentKey,
              storageUri: resolvedUri,
              documentUrl: status.documentUrl,
            })
          } else {
            dsStore.setPhase(item, 'staged')
          }
          const snap = dsStore.getSnapshot(item)
          if (snap?.documentSessionId && resolvedUri) {
            persistStagingMetaForFile(item, {
              documentSessionId: snap.documentSessionId,
              storageUri: resolvedUri,
              documentKey: snap.documentKey,
              documentUrl: snap.documentUrl,
              callbackUrl: snap.callbackUrl,
              generation: snap.generation,
              dsDirty: true,
              fullPrepareBundle: false,
            })
          }
        } else if (classified === 'error') {
          markSessionErrorAbandoned(session.documentKey)
          dsStore.setPhase(item, 'error')
        }
      } catch {
        /* keep waiting — transient sync-state failure must not abort the action */
      }
    }
  }

  function getAttachmentsElementDataForRpc(options?: {
    mode?: 'getData' | 'getFullData'
    actionId?: unknown
  }) {
    const mode = options?.mode ?? 'getFullData'
    if (mode === 'getFullData' && isAttachmentUploadDebugEnabled()) {
      attachmentUploadDebugWarn('хук getAttachmentsElementDataForRpc', {
        mode,
        actionId: options?.actionId ?? null,
        elementName: deps.fieldName.value,
      })
    }
    if (
      mode === 'getData' &&
      shouldUseInlineAttachmentGetData(options?.actionId, {
        pageActions: deps.pageActions.value,
      })
    ) {
      const focused = deps.componentRef.value?.getFocusedAttachmentItem?.() ?? null
      return buildInlineAttachmentElementUniData(focused)
    }
    const items =
      mode === 'getFullData'
        ? uploadPendingAttachmentsForGetFullData()
        : getAttachmentItemsForUpload()
    return deps.componentRef.value?.swingGetData?.(items)
  }

  function appendBytesHook(fileName: string, base64Data: string): void {
    try {
      const bytes = Uint8Array.from(
        atob(String(base64Data).replace(/\r?\n/g, '')),
        (c: string) => c.charCodeAt(0),
      )
      const file = new File([bytes], fileName)
      const inst = deps.componentRef.value
      if (inst && typeof inst.appendFile === 'function') {
        inst.appendFile(file)
      }
    } catch (error) {
      console.error('[appendBytes] Failed to decode/append file:', error)
    }
  }

  function appendFileCopiesHook(attachments: unknown): void {
    const att = attachments as { rows?: unknown[]; value?: { rows?: unknown[] } }
    const rows = Array.isArray(att?.rows)
      ? att.rows
      : Array.isArray(att?.value?.rows)
        ? att.value.rows
        : []
    const inst = deps.componentRef.value
    if (!rows.length || !inst || typeof inst.appendFileCopies !== 'function') {
      return
    }
    inst.appendFileCopies(uniDataTableToFileItems({ rows }))
  }

  async function fetchAttachmentBlobForFile(
    file: FileItem,
    options?: { persistDownloadMeta?: boolean },
  ): Promise<Blob | null> {
    if (file.file instanceof File) {
      return file.file
    }

    const sessionId = deps.getSessionId()
    if (!sessionId) {
      console.warn('[DOWNLOAD_ATTACHMENT] Missing sessionId')
      return null
    }

    const uploadElementId = resolveUploadElementId()
    if (uploadElementId == null) {
      console.warn('[DOWNLOAD_ATTACHMENT] Missing elementId')
      return null
    }

    const fields = (file.fields ?? {}) as Record<string, unknown>
    const codeRaw = getScalarFromFields(fields, 'code', '_id', 'CODE', 'Code')
    const codeNum = Number(codeRaw)
    const hasServerCode = Number.isFinite(codeNum) && codeNum > 0
    const documentSessionId = resolveAttachmentDocumentSessionId(file, uploadElementId)
    if (!hasServerCode && !documentSessionId) {
      console.warn('[DOWNLOAD_ATTACHMENT] Missing file code and documentSessionId')
      return null
    }

    try {
      const result = await fetchPlatformAttachmentDownload({
        elementId: uploadElementId,
        sessionId,
        type: deps.currentPlatform.value,
        item: file,
      })
      if (!result) return null

      if (options?.persistDownloadMeta !== false) {
        persistStagingMetaForFile(file, result.meta)
      }
      return result.blob
    } catch (error) {
      console.error('[DOWNLOAD_ATTACHMENT] Download failed', error)
      return null
    }
  }

  /**
   * После prepare Р7 native `File` может отсутствовать на строке.
   * Скачать MW temp и зарегистрировать `File` до getFullData.
   */
  async function ensurePendingLocalFilesForUpload(): Promise<void> {
    if (deps.elementType.value !== 'FILEATTACHMENTS') return

    const elementMeta = parseFileAttachmentsElementMeta(deps.elementConfig.value)
    const uploadElementId = resolveUploadElementId()
    const items = overlayTapeTempFields(getAttachmentItemsForUpload())
    let changed = false
    const next = [...items]

    for (let i = 0; i < next.length; i++) {
      const item = next[i]!
      if (item.file instanceof File) continue
      if (!isAttachmentPendingUploadMissingLocalFile(item, elementMeta, uploadElementId)) {
        continue
      }
      const blob = await fetchAttachmentBlobForFile(item, { persistDownloadMeta: false })
      if (!blob) continue
      const name = resolveAttachmentDisplayFileName(item).trim() || 'attachment'
      const recovered = withAttachmentItemChanged({
        ...item,
        file: new File([blob], name, { type: blob.type || 'application/octet-stream' }),
      })
      resolveNativeFileRegistry()?.registerItem(recovered)
      next[i] = recovered
      changed = true
    }

    if (changed) {
      applyAttachmentItemsToStore(next, 'download-meta-merge')
      deps.componentRef.value?.syncAttachmentFileItems?.(next)
    }
  }

  function persistStagingMetaForFile(source: FileItem, meta: PlatformDownloadMeta): void {
    const uploadElementId = resolveUploadElementId()
    if (uploadElementId == null) return

    const items = getAttachmentItemsForUpload()
    const match = findMatchingAttachmentItem(items, source)
    if (!match) return

    const aligned = alignAttachmentClientRowKey(source, match)
    const updated = applyPlatformDownloadMeta(aligned, uploadElementId, meta)
    if (attachmentRowDownloadMetaEqual(match, updated)) {
      return
    }
    const nextItems = items.map((item) => (item === match ? updated : item))
    if (attachmentInputSyncDepth > 0) return
    applyAttachmentItemsToStore(nextItems, 'download-meta-merge')
    deps.componentRef.value?.syncAttachmentFileItems?.(nextItems)
  }

  let attachmentInputSyncDepth = 0

  /**
   * Обновление таблицы вложений из MFileInput: store + sync UI без рекурсивного emit.
   * Guard `attachmentInputSyncDepth` — защита от цикла sync → emit → handleInput.
   */
  function handleInput(items: FileItem[]) {
    if (attachmentInputSyncDepth > 0) return
    attachmentInputSyncDepth += 1
    try {
      applyAttachmentItemsToStore(items, 'user-input')
      deps.componentRef.value?.syncAttachmentFileItems?.(items)
    } finally {
      attachmentInputSyncDepth -= 1
    }
  }

  /**
   * Обработчик таймера `awaiting_ws`: `POST document-sync-state` → ready / syncing dialog / stale|error.
   * Регистрируется через `registerR7AwaitingWsTimeoutHandler` при первом open/WS.
   */
  async function handleAwaitingWsTimeout(session: R7AttachmentEditSession): Promise<void> {
    const sessionContext = collectAttachmentSessionContext()
    const resolved = resolveAttachmentUploadSessionId(sessionContext)
    const sessionId = resolved.sessionId ?? deps.getSessionId()
    if (!sessionId) {
      markSessionErrorAbandoned(session.documentKey)
      notification.error({
        summary: tGlobal('attachments.r7WaitingSummary'),
        detail: tGlobal('attachments.r7NoSessionId'),
      })
      return
    }

    const platformSession = getPlatformFileSessionContext(
      deps.pageData.value as { isClone?: boolean; cloneOf?: string | null },
      sessionId,
      deps.currentPlatform.value,
    )

    const documentSessionId = session.documentSessionId?.trim()
    if (!documentSessionId) {
      markSessionErrorAbandoned(session.documentKey)
      notification.error({
        summary: tGlobal('attachments.r7WaitingSummary'),
        detail: tGlobal('attachments.r7NoDocumentSession'),
      })
      return
    }

    const dsStore = getDsAttachmentStore(session.elementId)
    const items = getAttachmentItemsForUpload()
    const row =
      items.find((item) => dsStore.getSnapshot(item)?.documentSessionId === documentSessionId)
      ?? items.find((item) => {
        const cv = resolveAttachmentRowCodeVersion((item.fields ?? {}) as Record<string, unknown>)
        return cv != null && cv.code === session.code && cv.version === session.version
      })
      ?? null
    try {
      const status = await fetchOfficeAttachmentDocumentSyncState({
        elementId: session.elementId,
        documentSessionId,
        session: platformSession,
      })

      switch (status.syncState) {
        case 'ready': {
          const resolvedUri = status.storageUri?.trim() || session.lastStorageUri?.trim() || ''
          reconcileSessionStaged(session.documentKey, resolvedUri, status.dsStatus)
          if (row && resolvedUri && status.documentKey) {
            dsStore.applyWsStaged(row, {
              documentKey: status.documentKey,
              storageUri: resolvedUri,
              documentUrl: status.documentUrl,
            })
          }
          notifyR7AttachmentStagingUpdated(
            session.code,
            session.version,
            resolvedUri,
            status.documentKey ?? session.documentKey,
          )
          notification.info({
            summary: tGlobal('attachments.r7SaveDoneSummary'),
            detail: tGlobal('attachments.r7SaveDoneDetail'),
          })
          return
        }
        case 'syncing': {
          const choice = await showR7SaveInProgressDialog()
          if (choice === 'wait') {
            resetAwaitingWsTimer(session.documentKey)
            return
          }
          try {
            await discardOfficeAttachmentDocument({
              elementId: session.elementId,
              documentSessionId,
              session: platformSession,
            })
          } catch (error) {
            console.warn('[R7 attachment] discard staging failed', error)
          }
          markSessionErrorAbandoned(session.documentKey)
          if (row) recoverAttachmentAfterError(row)
          notification.warn({
            summary: tGlobal('attachments.r7SaveCancelSummary'),
            detail: tGlobal('attachments.r7SaveCancelDetail'),
          })
          return
        }
        case 'stale':
        case 'error':
          markSessionErrorAbandoned(session.documentKey)
          if (row) dsStore.setPhase(row, 'error')
          notification.error({
            summary: tGlobal('attachments.r7SaveErrorSummary'),
            detail:
              status.message ??
              tGlobal('attachments.r7SaveErrorDetail'),
          })
          return
        default:
          break
      }
    } catch {
      markSessionErrorAbandoned(session.documentKey)
      if (row) dsStore.setPhase(row, 'error')
      notification.error({
        summary: tGlobal('attachments.r7WaitingSummary'),
        detail: tGlobal('attachments.r7StatusCheckFailed'),
      })
    }
  }

  function ensureAwaitingWsTimeoutHandler(): void {
    const uploadElementId = resolveUploadElementId()
    if (uploadElementId == null) return
    if (registeredTimeoutElementId === uploadElementId) return
    if (registeredTimeoutElementId != null) {
      unregisterR7AwaitingWsTimeoutHandler(registeredTimeoutElementId)
    }
    registeredTimeoutElementId = uploadElementId
    registerR7AwaitingWsTimeoutHandler(uploadElementId, handleAwaitingWsTimeout)
  }

  /** Доступна кнопка «Открыть в Р7» по meta элемента и строке (WUI-305). */
  function canOpenR7ForFile(item: FileItem): boolean {
    ensureAwaitingWsTimeoutHandler()
    const elementMeta = parseFileAttachmentsElementMeta(deps.elementConfig.value)
    return canOpenOrViewAttachmentInR7Editor(item, elementMeta, resolveUploadElementId())
  }

  /** Inline Office preview в зоне превью MFileInput (WUI-317). */
  function canPreviewR7InlineForFile(item: FileItem): boolean {
    ensureAwaitingWsTimeoutHandler()
    const elementMeta = parseFileAttachmentsElementMeta(deps.elementConfig.value)
    return canPreviewAttachmentInR7Inline(item, elementMeta, resolveUploadElementId())
  }

  /** Разбор политики Р7 для отладки ленты (`debug_attachment_tape`). */
  function resolveAttachmentTapeR7Debug(item: FileItem) {
    const elementMeta = parseFileAttachmentsElementMeta(deps.elementConfig.value)
    return explainAttachmentR7PolicyForTape(item, elementMeta, resolveUploadElementId())
  }

  function resolveR7EditorMode(item: FileItem): 'edit' | 'view' {
    const elementMeta = parseFileAttachmentsElementMeta(deps.elementConfig.value)
    return resolveAttachmentR7EditorMode(item, elementMeta, resolveUploadElementId())
  }

  function resolveAttachmentSyncVisualState(item: FileItem): DsAttachmentPhase | null {
    const uploadElementId = resolveUploadElementId()
    if (uploadElementId != null && isAttachmentR7PreviewAwaitingFinalSave(item, uploadElementId)) {
      return 'awaiting_ws'
    }
    if (uploadElementId != null) {
      const session = findSessionForAttachmentItem(item, uploadElementId)
      if (session?.phase === 'error') {
        return 'error'
      }
    }
    const dsStore = resolveDsStore()
    if (!dsStore) return null
    const phase = dsStore.getPhase(item)
    return phase === 'none' ? null : phase
  }

  function resolveAttachmentTapeBadgeUi(item: FileItem): AttachmentTapeBadgeUi {
    const elementMeta = parseFileAttachmentsElementMeta(deps.elementConfig.value)
    const resolved = findMatchingAttachmentItem(getAttachmentItemsForUpload(), item) ?? item
    return resolveAttachmentTapeRowBadge({
      item: resolved,
      meta: elementMeta,
      elementId: resolveUploadElementId(),
      resolvePhase: resolveAttachmentSyncVisualState,
    })
  }

  /** Актуальная строка из store, не устаревшая ссылка из preview (version мог измениться после upload). */
  function resolveAttachmentItemFromStore(file: FileItem): FileItem {
    const items = getAttachmentItemsForUpload()
    return findMatchingAttachmentItem(items, file) ?? file
  }

  /** sessionId, elementId и clone-контекст для `POST office-editor/prepare`. */
  function resolveR7EditorOpenContext(): {
    elementId: number
    elementName: string
    session: ReturnType<typeof getPlatformFileSessionContext>
  } | null {
    const sessionContext = collectAttachmentSessionContext()
    const resolved = resolveAttachmentUploadSessionId(sessionContext)
    const sessionId = resolved.sessionId ?? deps.getSessionId()
    const uploadElementId = sessionContext.elementId
    const elementName = deps.fieldName.value
    if (!sessionId || uploadElementId == null || !elementName) {
      return null
    }
    const session = getPlatformFileSessionContext(
      deps.pageData.value as { isClone?: boolean; cloneOf?: string | null },
      sessionId,
      deps.currentPlatform.value,
    )
    return { elementId: uploadElementId, elementName, session }
  }

  /**
   * Хук `onOfficeCallbackStatus`: `onWsCallback` → обновление DS-снимка (не `fields.storage_uri`).
   */
  function handleOfficeCallbackStatus(event: OfficeCallbackStatusEvent): void {
    ensureAwaitingWsTimeoutHandler()
    const uploadElementId = resolveUploadElementId() ?? event.elementId
    if (uploadElementId == null) return

    const dsStore = getDsAttachmentStore(uploadElementId)
    const { ignored } = onWsCallback(event)

    if (!ignored) {
      const items = getAttachmentItemsForUpload()
      const row = resolveAttachmentRowForOfficeWsEvent(
        items,
        event.documentSessionId,
        dsStore,
      )
      if (row) {
        const wasAwaitingWs = dsStore.getPhase(row) === 'awaiting_ws'
        if (isOfficeCallbackSaveReady(event)) {
          if (event.storageUri) {
            const applied = dsStore.applyWsStaged(row, {
              documentKey: event.documentKey ?? dsStore.getSnapshot(row)?.documentKey ?? '',
              storageUri: event.storageUri,
              documentUrl: event.documentUrl ?? undefined,
            })
            // lookup-miss не должен оставлять awaiting_ws и уводить бейдж в «новый».
            if (!applied) {
              dsStore.setPhase(row, 'staged')
            }
          } else if (wasAwaitingWs || dsStore.getPhase(row) === 'editing') {
            dsStore.setPhase(row, 'staged')
          }
          const snap = dsStore.getSnapshot(row)
          if (snap?.documentSessionId && event.storageUri) {
            persistStagingMetaForFile(row, {
              documentSessionId: snap.documentSessionId,
              storageUri: event.storageUri,
              documentKey: snap.documentKey,
              documentUrl: snap.documentUrl,
              callbackUrl: snap.callbackUrl,
              generation: snap.generation,
              dsDirty: true,
              fullPrepareBundle: false,
            })
          }
        } else if (isOfficeCallbackTerminalError(event)) {
          dsStore.setPhase(row, 'error')
        } else if (isOfficeCallbackNoChanges(event)) {
          // status 4: без изменений — вернуть preOpen (не «новый» platform_pending, если был staged).
          const session = findSessionForAttachmentItem(row, uploadElementId)
          const restore = session?.preOpenDsPhase
          if (
            restore
            && restore !== 'none'
            && restore !== 'editing'
            && restore !== 'downloading'
            && restore !== 'awaiting_ws'
          ) {
            dsStore.setPhase(row, restore)
          } else {
            dsStore.setPhase(row, 'idle')
          }
          if (wasAwaitingWs) {
            const snap = dsStore.getSnapshot(row)
            if (snap?.storageUri) {
              const cv = resolveAttachmentRowCodeVersion((row.fields ?? {}) as Record<string, unknown>)
              notifyR7AttachmentStagingUpdated(
                cv?.code ?? 0,
                cv?.version ?? 0,
                snap.storageUri,
                snap.documentKey,
              )
            }
          }
        }
      }

      if (row && isOfficeCallbackSaveReady(event) && event.storageUri) {
        const cv = resolveAttachmentRowCodeVersion((row.fields ?? {}) as Record<string, unknown>)
        notifyR7AttachmentStagingUpdated(
          cv?.code ?? 0,
          cv?.version ?? 0,
          event.storageUri,
          event.documentKey ?? undefined,
        )
      }
    }

    if (!ignored) {
      dispatchR7EditorOfficeCallback(event)
    }
  }

  const fileAttachmentHooks = {
    appendBytes: appendBytesHook,
    appendFileCopies: appendFileCopiesHook,
    getData: (actionId?: unknown) =>
      getAttachmentsElementDataForRpc({ mode: 'getData', actionId }),
    getFullData: () => getAttachmentsElementDataForRpc({ mode: 'getFullData' }),
    onOfficeCallbackStatus: handleOfficeCallbackStatus,
  }

  return {
    hasUploadableNativeFileForFormSave,
    getAttachmentItemsForUpload,
    applyAttachmentItemsToStore,
    recoverAttachmentAfterError,
    getDsAttachmentStore: resolveDsStore,
    uploadPendingAttachmentsForGetFullData,
    waitUntilR7NotBlockingGetFullData,
    abortIfR7FormSaveWaitNotReady,
    requestOfficeEditorForceSave,
    getAttachmentsElementDataForRpc,
    appendBytesHook,
    appendFileCopiesHook,
    fetchAttachmentBlobForFile,
    ensurePendingLocalFilesForUpload,
    persistStagingMetaForFile,
    handleInput,
    canOpenR7ForFile,
    canPreviewR7InlineForFile,
    resolveAttachmentTapeR7Debug,
    resolveR7EditorMode,
    resolveAttachmentSyncVisualState,
    resolveAttachmentTapeBadgeUi,
    resolveAttachmentItemFromStore,
    resolveR7EditorOpenContext,
    fileAttachmentHooks,
  }
}
